import React, { useState } from 'react';

/**
 * SessionDialog Component
 * 
 * Displays notifications about session changes with optional action buttons.
 * Styled to match ConfirmationModal.jsx (Dark Mode / App Theme).
 * 
 * Props:
 * - message: Dialog message text
 * - type: 'info' | 'warning' | 'action'
 * - onClose: Callback when dialog closes
 * - autoDismiss: (Ignored in this new version per user request to remove timer)
 * - onConfirm: Callback when "Logout Previous Device" is clicked
 * - onCancel: Callback when "Cancel" is clicked
 * - showButtons: Whether to show action buttons
 */
export default function SessionDialog({
    message,
    type = 'info',
    onClose,
    onConfirm = null,
    onCancel = null,
    showButtons = false
}) {
    const [isConfirming, setIsConfirming] = useState(false);
    const [isCanceling, setIsCanceling] = useState(false);

    // Handle confirm button click
    const handleConfirm = async () => {
        setIsConfirming(true);
        try {
            if (onConfirm) {
                await onConfirm();
            }
            if (onClose) onClose();
        } catch (error) {
            console.error('Error confirming session action:', error);
            setIsConfirming(false);
        }
    };

    // Handle cancel button click
    const handleCancel = async () => {
        setIsCanceling(true);
        try {
            if (onCancel) {
                await onCancel();
            }
            if (onClose) onClose();
        } catch (error) {
            console.error('Error canceling session action:', error);
            setIsCanceling(false);
        }
    };

    const isAction = type === 'action' || showButtons;

    // Use specific styles matching ConfirmationModal
    // type 'warning' -> isDangerous style (reds)
    // type 'action' -> accent style (blues/purples)
    const isWarning = type === 'warning';

    const title = isWarning ? 'Session Alert' : isAction ? 'Device Login Detected' : 'Session Notice';

    return (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[9999] backdrop-blur-xl">
            <div className="bg-secondary border border-accent/20 rounded-lg p-6 max-w-sm w-full mx-4 shadow-modal animate-fade-in-scale">
                {/* Header */}
                <div className="mb-4">
                    <h2 className="text-lg font-bold text-text-primary">{title}</h2>
                </div>

                {/* Message */}
                <div className="mb-6">
                    {(message.includes('Warning:') || message.includes('Note:')) ? (
                        <p className="text-text-secondary text-sm leading-relaxed">
                            {message.split(/Warning:|Note:/)[0]}
                            <span className="text-red-500 font-medium block mt-2">
                                {message.includes('Warning:') ? 'Warning:' : 'Note:'}{message.split(/Warning:|Note:/)[1]}
                            </span>
                        </p>
                    ) : (
                        <p className="text-text-secondary text-sm leading-relaxed">{message}</p>
                    )}
                </div>

                {/* Buttons (Only if action buttons are requested) */}
                {isAction && (onConfirm || onCancel) ? (
                    <div className="flex gap-3">
                        {/* Cancel Button */}
                        {onCancel && (
                            <button
                                onClick={handleCancel}
                                disabled={isCanceling || isConfirming}
                                className="flex-1 px-4 py-2 rounded-lg border border-accent/30 text-text-secondary hover:text-text-primary hover:border-accent/60 transition-colors duration-200 font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isCanceling ? (
                                    <span className="flex items-center justify-center gap-2">
                                        <span className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
                                        Canceling...
                                    </span>
                                ) : (
                                    'Cancel'
                                )}
                            </button>
                        )}

                        {/* Confirm Button */}
                        {onConfirm && (
                            <button
                                onClick={handleConfirm}
                                disabled={isConfirming || isCanceling}
                                className="flex-1 px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed bg-accent/20 border border-accent text-accent hover:bg-accent/30 hover:shadow-md"
                            >
                                {isConfirming ? (
                                    <span className="flex items-center justify-center gap-2">
                                        <span className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
                                        Logging out...
                                    </span>
                                ) : (
                                    'Logout'
                                )}
                            </button>
                        )}
                    </div>
                ) : (
                    // Close button for info/warning dialogs
                    onClose && (
                        <div className="flex justify-end">
                            <button
                                onClick={onClose}
                                className="px-4 py-2 rounded-lg border border-white/10 text-text-secondary hover:text-text-primary hover:bg-white/5 transition-colors font-medium text-sm"
                            >
                                Close
                            </button>
                        </div>
                    )
                )}
            </div>
        </div>
    );
}
